import {Inject, Injectable} from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  QueryDocumentSnapshot
} from '@angular/fire/compat/firestore';
import {Router} from '@angular/router';
import firebase from 'firebase/compat/app';

import {distinctUntilChanged, firstValueFrom, Observable, Subject, throttleTime} from 'rxjs';
import {map, take, takeUntil} from 'rxjs/operators';
import _ from 'underscore';
import {AventiObject} from '../models/AventiObject';
import {DbpathService} from './dbpath.service';
import {IdService} from './id.service';
import {SharedService} from './shared.service';
import {SnackbarService} from './snackbar.service';
import {DateService} from './date.service';
import {SortService} from './sort.service';
import {ChecklistService} from './checklist.service';
import {AngularFireDatabase} from '@angular/fire/compat/database';
import {UserService} from 'src/app/services/user.service';
import {Timestamp} from 'firebase/firestore';
import {getFunctions, httpsCallable} from '@angular/fire/functions';
import {FirebaseApp} from '@angular/fire/app';
import FieldValue = firebase.firestore.FieldValue;
import {RtdbService} from 'src/app/services/rtdb.service';

export interface ImportObjectsRequest {
  projectId: string;
  objects: AventiObject[];
  user: any;
}

export interface ImportObjectsResponse {
  success: boolean;
}


@Injectable()
export class ObjectService {
  object: AventiObject;
  private importFn: (data: ImportObjectsRequest) => Promise<{ data: ImportObjectsResponse }>;

  constructor(
    @Inject(FirebaseApp) private app: FirebaseApp,
    // private firestore: Firestore,
    private afs: AngularFirestore,
    private idservice: IdService,
    private dbpathservice: DbpathService,
    private sharedservice: SharedService,
    private snackbarservice: SnackbarService,
    private router: Router,
    private dateservice: DateService,
    private sortservice: SortService,
    private checklistservice: ChecklistService,
    private rtdb: AngularFireDatabase,
    private userservice: UserService,
    private rtdbservice: RtdbService
  ) {
    this.object = new AventiObject();
    const functions = getFunctions(this.app, 'europe-west1');
    this.importFn = httpsCallable<ImportObjectsRequest, ImportObjectsResponse>(
      functions,
      'importObjectsToProject'
    );
  }

  rtdbGetObjects(
    projectId: string,
    destroy$: Subject<unknown>
  ): Observable<AventiObject[]> {
    const cleanKey = this.rtdbservice.encodeRtdbKey(projectId);
    const objectsRef = this.rtdb.database.ref(`projects/${cleanKey}/objects`);

    return new Observable<AventiObject[]>(observer => {
      let lastPayload: string | undefined;

      const listener = objectsRef.on(
        'value',
        snapshot => {
          const data = snapshot.val();
          // build your array
          const arr = data && typeof data === 'object'
            ? Object.entries(data).map(([id, v]) => ({
              id,
              ...(v as object)
            }))
            : [];

          // serialize and compare
          const json = JSON.stringify(arr);
          if (json === lastPayload) {
            return; // no real change → do nothing
          }
          lastPayload = json;
          observer.next(arr);
        },
        err => observer.error(err)
      );

      // cleanup when destroy$ emits
      const sub = destroy$.subscribe(() => {
        objectsRef.off('value', listener);
        observer.complete();
        sub.unsubscribe();
      });

      // also cleanup if someone unsubscribes directly
      return () => {
        objectsRef.off('value', listener);
        sub.unsubscribe();
      };
    }).pipe(
      // throttle to at most one UI update per second
      throttleTime(1000, undefined, {leading: true, trailing: true}),
      // ensure we tear down if destroy$ emits
      takeUntil(destroy$)
    );
  }

  rtdbGetObjectsIncremental(
    projectId: string,
    destroy$: Subject<unknown>
  ): Observable<AventiObject[]> {
    const cleanKey = this.rtdbservice.encodeRtdbKey(projectId);
    const objectsRef = this.rtdb.database.ref(`projects/${cleanKey}/objects`);

    return new Observable<AventiObject[]>(observer => {
      const map = new Map<string, AventiObject>();
      let seeded = false;

      // 1) Seed from a single .once('value')
      objectsRef.once('value')
        .then(snap => {
          const data = (snap.val() as Record<string, any>) || {};
          for (const [id, val] of Object.entries(data)) {
            map.set(id, {id, ...(val as object)});
          }
          observer.next(Array.from(map.values()));
          seeded = true;

          // 2) Incremental listeners
          const onAdd = (snap: any) => {
            if (!seeded) {
              return;
            }
            const id = snap.key;
            if (!map.has(id)) {
              map.set(id, {id, ...(snap.val() || {})});
              observer.next(Array.from(map.values()));
            }
          };
          const onChange = (snap: any) => {
            if (!seeded) {
              return;
            }
            const id = snap.key;
            const newObj = {id, ...(snap.val() || {})};
            const prev = map.get(id);
            if (!prev || JSON.stringify(prev) !== JSON.stringify(newObj)) {
              map.set(id, newObj);
              observer.next(Array.from(map.values()));
            }
          };
          const onRemove = (snap: any) => {
            if (!seeded) {
              return;
            }
            if (map.delete(snap.key)) {
              observer.next(Array.from(map.values()));
            }
          };

          objectsRef.on('child_added', onAdd);
          objectsRef.on('child_changed', onChange);
          objectsRef.on('child_removed', onRemove);

          // teardown when destroy$ fires
          const sub = destroy$.subscribe(() => {
            objectsRef.off();
            observer.complete();
            sub.unsubscribe();
          });
        })
        .catch(err => observer.error(err));

      // also cleanup on unsubscribe
      return () => objectsRef.off();
    }).pipe(
      // make sure we only ever emit at most once per second…
      throttleTime(1000, undefined, {leading: true, trailing: true}),
      // …and only if the array actually changed since the last emit
      distinctUntilChanged((a, b) =>
        a.length === b.length &&
        a.every((obj, i) => JSON.stringify(obj) === JSON.stringify(b[i]))
      )
    );
  }

  getObjectFiles(objectid: string) {
    const projectid = this.idservice.projectId();
    return this.afs
      .collection(
        this.dbpathservice.dbPathObjectFiles(objectid, projectid),
        (ref) =>
          ref
            .where('projectid', '==', projectid)
            .orderBy('name')
      )
      .valueChanges({idField: 'id'});
  }

  getObjectsByProjectId(projectid?: string): Observable<AventiObject[]> {
    if (!projectid) {
      projectid = this.idservice.projectId();
    }
    return this.afs
      .collection(this.dbpathservice.dbPathObjects(projectid), (ref) =>
        ref
          .where('projectid', '==', projectid)
          .orderBy('name')
      ).snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((action) => {
            const id = action.payload.doc.id;
            const object = {...action.payload.doc.data() as AventiObject};
            object.editedBy = this.dateservice.getLatestEditedByFromArray(object.editedBy);
            const fromCache = (action.payload.doc as firebase.firestore.DocumentSnapshot).metadata.fromCache;
            return {id, fromCache, ...object} as AventiObject;
          });
        }));
  }

  async getObjectsByIds(objectIds: string[]): Promise<AventiObject[]> {
    if (!objectIds || objectIds.length === 0) {
      return [];
    }

    const results: AventiObject[] = [];
    const chunkSize = 10; // Firestore's limit for 'in' queries

    for (let i = 0; i < objectIds.length; i += chunkSize) {
      const chunk = objectIds.slice(i, i + chunkSize);
      const query = this.afs
        .collection(this.dbpathservice.dbPathObjects(this.idservice.projectId()), (ref) =>
          ref.where('__name__', 'in', chunk)
        )
        .get();

      const snapshot = await firstValueFrom(query);
      snapshot.forEach((doc) => {
        const id = doc.id;
        const object = {...doc.data() as AventiObject};
        object.editedBy = this.dateservice.getLatestEditedByFromArray(object.editedBy);
        results.push({id, ...object});
      });
    }

    return results;
  }

  batchGetObjectsByProjectId(projectid?: string): Observable<AventiObject[]> {
    if (!projectid) {
      projectid = this.idservice.projectId();
    }

    const batchSize = 500;
    let lastDocument: QueryDocumentSnapshot<any> | null = null;
    const fetchedObjects: AventiObject[] = [];
    let actionsLength = batchSize + 1;

    return new Observable<AventiObject[]>((observer) => {
      const fetchData = (startAfter: QueryDocumentSnapshot<any> | null) => {
        const query = this.afs.collection(
          this.dbpathservice.dbPathObjects(projectid),
          (ref) =>
            ref
              .where('projectid', '==', projectid)
              .orderBy('name')
              .limit(batchSize)
              .startAfter(startAfter)
        );

        query.snapshotChanges().pipe(take(1)).subscribe(actions => {
          actionsLength = actions.length;
          actions.forEach((action) => {
            const id = action.payload.doc.id;
            const object = {...action.payload.doc.data() as AventiObject};
            object.editedBy = this.dateservice.getLatestEditedByFromArray(object.editedBy);
            const fromCache = (action.payload.doc as firebase.firestore.DocumentSnapshot).metadata.fromCache;
            fetchedObjects.push({id, fromCache, ...object} as AventiObject);
            lastDocument = action.payload.doc;
          });

          observer.next(fetchedObjects);
          console.log(actionsLength);

          if (actions.length === 0 || actions.length < batchSize) {
            console.log('COMPLETE');
            observer.complete();
          } else {
            console.log(lastDocument?.id);
            fetchData(lastDocument);
          }
        });
      };
      fetchData(null);
    });
  }

  getObjectsByProjectIdPromise(projectid?: string, limit: number = 1000, iteration: number = 0): Promise<AventiObject[]> {
    if (!projectid) {
      projectid = this.idservice.projectId();
    }
    return firstValueFrom(this.afs
      .collection(this.dbpathservice.dbPathObjects(projectid), (ref) =>
          ref
            .where('projectid', '==', projectid)
            .orderBy('name')
            .startAfter(iteration * limit)
            .endAt(iteration * limit + limit)
        // .limit(limit)
      ).get()
      .pipe(
        map((actions) => {
          return actions.docs.map((action) => {
            const id = action.id;
            const object = {...action.data() as AventiObject};
            object.editedBy = this.dateservice.getLatestEditedByFromArray(object.editedBy);
            const fromCache = action.metadata.fromCache;
            return {id, fromCache, ...object} as AventiObject;
          });
        })));
  }

  async set(object: AventiObject, redirect: boolean = false, createDespiteContainingId: boolean = false) {
    object = _.omit(object, 'checklists');
    object = _.omit(object, 'files');
    object = _.omit(object, 'pdfexports');
    const projectid = object.projectid;
    const col = this.getObjectCollectionByProjectId(projectid);

    this.sharedservice.user.pipe(take(1)).subscribe(async (u) => {
      if (u != null) {
        const user = u;
        const timestamp = Timestamp.now();
        const usertimestamp = {
          user,
          timestamp,
        };
        if (!object.editedBy) {
          object.editedBy = [];
        }
        object.editedBy.push(usertimestamp);
        object.editedBy = this.sortservice.reduceArrayToMaxLength(object.editedBy, 'timestamp');

        object.lastupdated = timestamp;
        object.name = object.name.replace(/\r\n/g, '');
        console.log('Ready to set object');
        if (object.id && !createDespiteContainingId) {
          const ref = col.doc(object.id).ref;
          await ref
            .update(object)
            .then((x) => {
              return x;
            })
            .catch((err) => {
              return err;
            });
        } else {
          const docRefObject = this.afs.firestore
            .collection(
              this.dbpathservice.dbPathObjects(projectid)
            )
            .doc();
          let item: AventiObject;
          object.id = docRefObject.id;
          item = object;
          Object.keys(item).forEach(
            (key) => item[key] === null && delete item[key]
          );
          console.log('Setting object', object);
          await docRefObject
            .set(object)
            .then((o) => {
              console.log('object added', o);
            })
            .catch((err) => {
              return err;
            });
          this.sharedservice.objects
            .pipe(take(1))
            .subscribe((objects) => {
              if (objects !== null) {
                objects.push(object);
                this.sharedservice.objects.next(objects);
                if (redirect) {
                  this.router.navigateByUrl(
                    'p/' + object.projectid + '/o/' + object.id
                  );
                  this.sharedservice.isLoading.next([false, '', ['']]);
                } else {
                  this.snackbarservice.openSnackBar(
                    'Objekt opprettet: ' + object.name,
                    ''
                  );
                }
              }
            });
        }
      }
    });
  }

  async updatePartialObjects(objects: Partial<AventiObject>[]) {
    if (!objects || objects.length === 0) return;
    const user = (await firstValueFrom(this.sharedservice.user));
    const timestamp = Timestamp.now();
    const usertimestamp = {
      user: { id: user.id },
      timestamp,
    };
    const batchSize = 500;
    for (let i = 0; i < objects.length; i += batchSize) {
      const batchObjects = objects.slice(i, i + batchSize);
      const batch = this.afs.firestore.batch();
      for (const obj of batchObjects) {
        if (!obj.id || !obj.projectid) continue;
        const path = this.dbpathservice.dbPathObject(obj.id, obj.projectid);
        const ref = this.afs.doc(path).ref;
        // Prepare update data dynamically
        const updateData: any = { ...obj };
        updateData.editedBy = [usertimestamp];
        updateData.lastupdated = timestamp;
        batch.update(ref, updateData);
      }
      await batch.commit();
    }
  }

  async updatePartialObject(objectId: string, object: Partial<AventiObject>) {
    object = _.omit(object, 'checklists');
    object = _.omit(object, 'files');
    object = _.omit(object, 'pdfexports');
    const user = (await firstValueFrom(this.sharedservice.user));
    const timestamp = Timestamp.now();
    const usertimestamp = {
      user: {id: user.id},
      timestamp,
    };
    object.editedBy = [usertimestamp];

    object.lastupdated = timestamp;
    const path = this.dbpathservice.dbPathObject(objectId, object.projectid);
    console.log(`Updating partial object at path: ${path}`, object);
    await this.afs.doc(path).update(object);
  }


  // Function to update a field
  async updateField(id: string, field: string, data: any) {
    if (!id || !field) {
      throw new Error('Invalid parameters provided to updateField.');
    }

    const path = this.dbpathservice.dbPathObject(id);
    const updateObject = {
      [field]: data
    };

    try {
      await this.afs.doc(path).update(updateObject);
      console.log(`Field "${field}" updated successfully for document ID "${id}".`);
    } catch (error) {
      console.error('Error updating field:', error);
      throw error;
    }
  }

  // Function to delete a field
  async deleteField(id: string, field: string) {
    if (!id || !field) {
      throw new Error('Invalid parameters provided to deleteField.');
    }

    const path = this.dbpathservice.dbPathObject(id);
    const updateObject = {
      [field]: FieldValue.delete()
    };

    try {
      await this.afs.doc(path).update(updateObject);
      console.log(`Field "${field}" deleted successfully for document ID "${id}".`);
    } catch (error) {
      console.error('Error deleting field:', error);
      throw error;
    }
  }

  async deleteObject(object: AventiObject) {
    let id: string;
    if (object) {
      id = object.id;
    } else {
      id = this.idservice.objectId();
    }
    const checklists = await firstValueFrom(this.sharedservice.checklists);
    for (const checklist of checklists) {
      if (checklist.objectid === id) {
        await this.checklistservice.deleteChecklist(checklist, false);
      }
    }
    const path = this.dbpathservice.dbPathObject(id);
    await this.afs.doc(path).delete();
  }

  async importObjects(objectsInput: AventiObject[]) {
    if (!objectsInput?.length) {
      this.snackbarservice.openSnackBar('Ingen objekter å importere.', null);
      return;
    }

    // prepare payload
    const projectId = this.idservice.projectId();
    const objects: AventiObject[] = objectsInput.map(obj => ({
      ...obj,
      projectid: projectId
    }));
    const user = await firstValueFrom(this.userservice.getUser());
    const payload = {projectId, objects, user};

    try {
      this.sharedservice.isLoading.next([
        true,
        'Vennligst vent...',
        [`Sender ${objects.length} objekter til import`],
      ]);

      // call the callable function
      const {data} = await this.importFn(payload);

      if (data.success) {
        this.snackbarservice.openSnackBar(
          'Objektene blir importert til prosjektet...',
          null
        );
      } else {
        throw new Error('Import mislyktes uten spesifikk feilmelding');
      }
    } catch (err: any) {
      console.error('Error during import:', err);
      this.snackbarservice.openSnackBar(
        'Noe gikk galt under importen av objektene.',
        null
      );
    } finally {
      this.sharedservice.isLoading.next([false, '', ['']]);
    }
  }


  getObjectByKey(projectid: string, key: string, value: string): Observable<AventiObject[]> {
    return this.afs
      .collection(this.dbpathservice.dbPathObjects(projectid), (ref) =>
        ref
          .where('projectid', '==', projectid)
          .where(key, '==', value)
          .orderBy('name')
      ).valueChanges({idField: 'id'})
      .pipe(
        map((objects) => {
          return objects.map((object: AventiObject) => {
            object.editedBy = this.dateservice.getLatestEditedByFromArray(object.editedBy);
            return object;
          });
        })
      );
  }

  getObjectById(projectid: string, objectId: string): Observable<AventiObject> {
    const doc: AngularFirestoreDocument<AventiObject> = this.afs.doc(this.dbpathservice.dbPathObject(objectId, projectid));
    return doc.snapshotChanges().pipe(
      map((action) => {
        const id = action.payload.id;
        const object = action.payload.data();
        const fromCache = action.payload.metadata.fromCache;
        return {id, fromCache, ...object} as AventiObject;
      }));
  }

  /**
   * Returns a list of all object names and ids for a given project.
   * @param projectid The project ID to filter objects by.
   * @returns Observable<{id: string, name: string}[]>
   */
  getObjectNameTfmRdsByProjectId(projectid?: string): Observable<{ id: string, name: string }[]> {
    if (!projectid) {
      projectid = this.idservice.projectId();
    }
    return new Observable<{ id: string, name: string }[]>((observer) => {
      this.afs.firestore
        .collection(this.dbpathservice.dbPathObjects(projectid))
        .orderBy('name')
        .get({source: 'server'}) // Disable cache by fetching directly from the server
        .then((snapshot) => {
          const objects = snapshot.docs.map((doc) => {
            const id = doc.id;
            const data = doc.data() as AventiObject;
            return {id, name: data.name, tfmTag: data.tfmTag, rdsTag: data.rdsTag};
          });
          observer.next(objects);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  private getObjectCollectionByProjectId(projectid?: string): AngularFirestoreCollection<AventiObject> {
    if (!projectid) {
      projectid = this.idservice.projectId();
    }
    return this.afs.collection('projects/' + projectid + '/objects', (ref) => {
      return ref;
    });
  }

  // async getObjectCount(projectid: string): Promise<number> {
  //   const colRef = collection(this.firestore, this.dbpathservice.dbPathObjects(projectid));
  //   const q = query(colRef, where('projectid', '==', projectid));
  //   const snapshot = await getCountFromServer(q);
  //   return snapshot.data().count;
  // }
  //
  // async getObjectTFMCount(projectid: string): Promise<number> {
  //   const colRef = collection(this.firestore, this.dbpathservice.dbPathObjects(projectid));
  //   const q = query(colRef,
  //     where('projectid', '==', projectid),
  //     where('tfmTag', '!=', null),
  //   );
  //   const snapshot = await getCountFromServer(q);
  //   return snapshot.data().count;
  // }
  //
  // async getObjectRDSCount(projectid: string): Promise<number> {
  //   const colRef = collection(this.firestore, this.dbpathservice.dbPathObjects(projectid));
  //   const q = query(colRef,
  //     where('projectid', '==', projectid),
  //     where('rdsTag', '!=', null),
  //   );
  //   const snapshot = await getCountFromServer(q);
  //   return snapshot.data().count;
  // }

}
